import React, {useCallback, useMemo} from 'react';

import {SvgIcon} from '@mui/material';
import {Dialog} from '@zoom/react-ui';
import {IconCalendar} from '@zoom/sun-ui-icon';
import {Trans, useTranslation} from 'react-i18next';
import {useSelector} from 'react-redux';
import {useNavigate} from 'react-router-dom';

import {
  Actions,
  Card,
  CardDivider,
  CardImg,
  CardImgContainer,
  TypographyBody2,
  TypographyH2,
} from './Style.js';

import CalendarIllustration from 'assets/svg/calendar-illustration-plain.svg';
import CalendarChecked from 'assets/svg/calendar-with-checkmark.svg';
import {ReactComponent as CloseOutlined} from 'assets/svg/close-outlined.svg';
import {ReactComponent as RescheduleOutlined} from 'assets/svg/scheduling-display-outlined.svg';
import {VIEW_STATE} from 'Components/Cancellations/Cancellations.js';
import EventInfo from 'Components/EventInfo';
import {ZmQuaternaryBtn, ZmTertiaryBtn} from 'Components/ZmButtons';
import useIsAutoRefundLimit from 'hooks/useIsAutoRefundLimit.js';
import {ADHOC_TYPE, CURRENCY_SYMBOL} from 'Utils/consts.js';
/**
 * Appointment Card Component
 * @param {Object} props
 * @return {ReactElement}
 */
export default function ApptCard(props) {
  const {
    anotherEventButtonContent,
    anotherEventButtonEnabled,
    attendeeId,
    confirmMessageKey,
    displayName,
    eventInfo,
    isBookerVoting,
    onCancel,
    onReschedule,
    timeZone,
  } = props;
  const navigate = useNavigate();
  const {t} = useTranslation();

  const anotherEventBtnText = useMemo(() => {
    // support default text localization
    if (!anotherEventButtonContent || anotherEventButtonContent === 'Schedule another event') {
      return t('cancellation.another');
    }
    return anotherEventButtonContent;
  }, [t, anotherEventButtonContent]);

  const currency = eventInfo?.attendees?.[0]?.currency;
  const currencySymbol = CURRENCY_SYMBOL[currency];
  const price = eventInfo?.attendees?.[0]?.totalFee;
  const displayPrice = price ? `${currencySymbol}${price}` : '';

  const isAutoRefundLimit = useIsAutoRefundLimit(eventInfo?.originalEvent?.start.dateTime, timeZone);

  const notificationEmail = useSelector((state) => state.bookAppointments.notificationEmail);

  const openRescheduleDialog = useCallback(() => {
    Dialog.confirm({
      title: t('payment.contactHostToReschedule'),
      content: <Trans
        t={t}
        i18nKey="payment.contactHostToRescheduleDesc"
        values={{email: notificationEmail}}
        // eslint-disable-next-line jsx-a11y/anchor-has-content
        components={{a: <a href={`mailto: ${encodeURIComponent(notificationEmail)}`} />}}
      />,
      okText: t('payment.contactHost'),
      cancelText: t('common.dismiss'),
      onOk: () => {
        window.open(`mailto: ${encodeURIComponent(notificationEmail)}`, '__blank');
      },
    });
  }, [notificationEmail, t]);

  const handleOnReschedule = useCallback(() => {
    if (price > 0 && isAutoRefundLimit) {
      openRescheduleDialog();
      return;
    }
    if (onReschedule) {
      onReschedule();
    } else {
      navigate(`/manage/${attendeeId}/rescheduling`);
    }
  }, [onReschedule, attendeeId, navigate, isAutoRefundLimit, openRescheduleDialog, price]);

  return (
    <Card>
      <CardImgContainer>{
        confirmMessageKey ?
        <CardImg src={CalendarChecked} alt="calendar"/> :
        <CardImg src={CalendarIllustration} alt="calendar"/>
      }</CardImgContainer>
      {confirmMessageKey ? <>
        {isBookerVoting ?
          <>
            <TypographyH2
              sx={{
                textAlign: 'center',
              }}
            >
              {t('polls.completedTitle')}
            </TypographyH2>
            <TypographyBody2
              sx={{
                textAlign: 'center',
              }}>
              {t('polls.completedSubHead')}
            </TypographyBody2>
          </> :
          <TypographyH2
            sx={{
              textAlign: 'center',
            }}
          >
            {t('common.confirmed')}
          </TypographyH2>
        }
        {displayName && <TypographyBody2
          sx={{
            textAlign: 'center',
          }}
        >
          {displayPrice && (
            <Trans
              t={t}
              i18nKey="payment.receivedPayment"
              defaults="We received your payment of {{price}}, <1/>"
              values={{price: displayPrice}}
              components={{1: <Trans
                t={t}
                i18nKey={confirmMessageKey}
                defaults="You are booked with <bold>{{name}}</bold>"
                values={{name: displayName}}
                components={{bold: <b />}}
                shouldUnescape={true}
              />}}
            >
            </Trans>
          )}
          {!displayPrice && <Trans
            t={t}
            i18nKey={confirmMessageKey}
            defaults="You are booked with <bold>{{name}}</bold>"
            values={{name: displayName}}
            components={{bold: <b />}}
            shouldUnescape={true}
          />}
        </TypographyBody2>}
      </> :
      <>
        {<Trans // when !confirmMessageKey
          t={t}
          i18nKey="common.manageYourApptWithHost"
          defaults="<header>Manage your appointment</header>\n<body>with <bold>{{name}}</bold></body>"
          values={{name: displayName}}
          components={{
            bold: <b />,
            header: <TypographyH2 sx={{textAlign: 'center'}} />,
            body: <TypographyBody2 sx={{textAlign: 'center'}} />,
          }}
          shouldUnescape={true}
        />}
      </>}

      <CardDivider />

      <EventInfo
        {...props}
      />

      {((onReschedule && onCancel) || attendeeId) && <Actions>
        {(![ADHOC_TYPE.ONE_OFF, ADHOC_TYPE.POLL].includes(props.eventInfo?.bookingMethod)) &&
        <ZmTertiaryBtn
          sx={{flexGrow: 1}}
          onClick={handleOnReschedule}
          startIcon={<SvgIcon fontSize="xsmall" component={RescheduleOutlined} inheritViewBox />}
          size="small"
        >
          {t('rescheduling.rescheduleAppt')}
        </ZmTertiaryBtn>}
        <ZmTertiaryBtn
          sx={{flexGrow: 1}}
          onClick={() => {
            if (onCancel) {
              onCancel();
            } else {
              navigate(`/manage/${attendeeId}/cancelling?state=${VIEW_STATE.FORM_VIEW}`);
            }
          }}
          startIcon={<SvgIcon fontSize="xsmall" component={CloseOutlined} inheritViewBox />}
          size="small"
        >
          {t('common.cancel')}
        </ZmTertiaryBtn>
      </Actions>}
      {anotherEventButtonEnabled && (
        <ZmQuaternaryBtn
          sx={{marginTop: '24px'}}
          size="small"
          startIcon={<IconCalendar size={14} />}
          // navigate(0) means reload current page
          onClick={() => navigate(0)}
        >
          {anotherEventBtnText}
        </ZmQuaternaryBtn>
      )}
    </Card>
  );
}
